import EmblaCarousel from 'embla-carousel';
import AutoHeight from 'embla-carousel-auto-height';
import Fade from 'embla-carousel-fade';

const defaultOptions = {
  duration: 40,
};
const activeSlideClass = 'embla__slide--active';
const activeThumbClass = 'embla-thumbs__item--active';

// Handler for updating active slide.
export const updateCarouselState = (emblaInstance, thumbsInstance = null) => {
  const currentSlideIndex = emblaInstance.selectedScrollSnap();
  const slides = emblaInstance.slideNodes();

  slides.forEach((slide, index) => {
    if (index === currentSlideIndex) {
      slide.classList.add(activeSlideClass);
    } else {
      slide.classList.remove(activeSlideClass);
    }
  });

  // Update thumbs as well (if available).
  if (thumbsInstance) {
    const thumbs = thumbsInstance.slideNodes();
    const thumbsNode = thumbsInstance.rootNode();
    const activeThumbIndicator = thumbsNode.querySelector(
      '.embla-thumbs__active-thumb-indicator',
    );

    thumbs.forEach((thumb, index) => {
      if (index === currentSlideIndex) {
        thumb.classList.add(activeThumbClass);

        // Move the indicator under the active thumb.
        if (activeThumbIndicator) {
          const containerOffset = thumbsInstance.internalEngine().target.get();
          activeThumbIndicator.style.left = `${
            thumb.offsetLeft + containerOffset
          }px`;
          activeThumbIndicator.style.width = `${thumb.offsetWidth}px`;
        }

        // If we are are at the last thumb, add a class for hiding the overlay pseudo.
        if (currentSlideIndex === thumbs.length - 1) {
          thumbsNode.classList.add('embla-thumbs__viewport--max-scroll');
        } else {
          thumbsNode.classList.remove('embla-thumbs__viewport--max-scroll');
        }
      } else {
        thumb.classList.remove(activeThumbClass);
      }
    });
  }
};

// Handler for updating button states.
export const updateNavState = (emblaInstance, emblaNode) => {
  const prevButton = emblaNode.querySelector('.embla__button--prev');
  const nextButton = emblaNode.querySelector('.embla__button--next');

  if (emblaInstance.canScrollPrev()) {
    prevButton.removeAttribute('disabled');
  } else {
    prevButton.setAttribute('disabled', 'disabled');
  }

  if (emblaInstance.canScrollNext()) {
    nextButton.removeAttribute('disabled');
  } else {
    nextButton.setAttribute('disabled', 'disabled');
  }
};

// Init a carousel.
export const initEmbla = (emblaNode, options = {}) => {
  const viewport = emblaNode.querySelector('.embla__viewport');
  return new EmblaCarousel(
    viewport,
    {
      ...defaultOptions,
      ...options,
    },
    [AutoHeight(), Fade()],
  );
};

// Init thumbs for a carousel.
export const initEmblaThumbs = (emblaInstance, emblaNode, options = {}) => {
  const thumbsViewport = emblaNode.querySelector('.embla-thumbs__viewport');
  const thumbsInstance = new EmblaCarousel(thumbsViewport, {
    containScroll: 'keepSnaps',
    dragFree: true,
    ...options,
  });

  // Thumbs click listener.
  const thumbs = thumbsInstance.slideNodes();
  thumbs.forEach((thumb, index) => {
    thumb.addEventListener(
      'click',
      () => {
        emblaInstance.scrollTo(index);
      },
      false,
    );
  });

  // Selection side effect: sync with main Embla instance.
  thumbsInstance.on('select', () => {
    const index = thumbsInstance.selectedScrollSnap();
    const mainEmblaIndex = emblaInstance.selectedScrollSnap();
    if (index !== mainEmblaIndex) {
      emblaInstance.scrollTo(index);
    }
  });

  return thumbsInstance;
};

// Init navigation buttons for a carousel.
export const initEmblaNav = (emblaInstance, emblaNode) => {
  const prevButton = emblaNode.querySelector('.embla__button--prev');
  const nextButton = emblaNode.querySelector('.embla__button--next');

  const scrollPrev = () => {
    emblaInstance.scrollPrev();
  };
  const scrollNext = () => {
    emblaInstance.scrollNext();
  };

  if (prevButton && nextButton) {
    prevButton.addEventListener('click', scrollPrev, false);
    nextButton.addEventListener('click', scrollNext, false);
  }

  // Return a method that removes the listeners.
  return () => {
    prevButton.removeEventListener('click', scrollPrev, false);
    nextButton.removeEventListener('click', scrollNext, false);
  };
};
